import { CardReason } from './payment-plan';

export interface AddCardCopy {
  formHeading: string;
  formBody?: string;
  formSubmitLabel: string;
  formFineprint: string;
}

export interface AddCardCopyContext {
  companyName?: string;
  lateCancellationFee?: number;
  noShowFee?: number;
}

const STRIPE_FINEPRINT = 'Your credit card is handled securely by Stripe®.';

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

/** Matches the reminder messages. */
const NOTICE_PERIOD = '1 hour';

function feeSentence({ lateCancellationFee, noShowFee }: AddCardCopyContext): string {
  const notice = `We require at least ${NOTICE_PERIOD} notice to reschedule or cancel.`;

  if (!lateCancellationFee || !noShowFee) {
    return `${notice} Missed appointments and same-day cancellations are charged to you.`;
  }

  return (
    `${notice} Missed appointments are charged ${formatCurrency(noShowFee)}, ` +
    `and same-day cancellations may incur a fee up to ${formatCurrency(lateCancellationFee)}.`
  );
}

/**
 * The default fineprint, "not charged until the order is complete", is untrue
 * for a fee backstop: that charge is triggered by the service *not* happening.
 */
export function addCardCopy(reason: CardReason, context: AddCardCopyContext = {}): AddCardCopy {
  switch (reason) {
    case CardReason.RecipientFees:
      return {
        formHeading: 'Card required to schedule',
        formBody:
          `${context.companyName || 'Your company'} is paying for this service, however we ` +
          `need a card to reserve your appointment. ${feeSentence(context)} ` +
          'Otherwise your card will not be charged.',
        formSubmitLabel: 'Save Card',
        formFineprint: STRIPE_FINEPRINT,
      };

    case CardReason.RecipientChargeAndFees:
      return {
        formHeading: 'Add a payment method',
        formBody: `Your card will be charged when the service is complete. ${feeSentence(context)}`,
        formSubmitLabel: 'Save Card',
        formFineprint: STRIPE_FINEPRINT,
      };

    case CardReason.RecipientCharge:
      return {
        formHeading: 'Add a payment method',
        formBody: 'Your card will be charged when the service is complete.',
        formSubmitLabel: 'Save Card',
        formFineprint: STRIPE_FINEPRINT,
      };

    case CardReason.CompanyPays:
    default:
      return {
        formHeading: 'Add Credit Card',
        formSubmitLabel: 'Save Card',
        formFineprint:
          'Your credit card is handled securely by Stripe®. ' +
          'Your card will not be charged until the order is complete.',
      };
  }
}
